import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { rxResource, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, EMPTY, map, Observable, Subject, switchMap, tap } from 'rxjs';
import { AuthService } from '@suite/shared';
import { CLIENT_SETTINGS_ENDPOINTS } from '@suite/client-settings-api';
import {
  ClientSettings,
  ClientSettingsRemoveValueRequest,
  ClientSettingsSaveRequest,
  ClientSettingsSetValueRequest,
} from '@suite/client-settings-models';

@Injectable({ providedIn: 'root' })
export class ClientSettingsService {
  private http = inject(HttpClient);
  private authService = inject(AuthService);

  private settings = signal<Record<string, unknown>>({});

  // Default storage key - can be made configurable if needed
  private readonly STORAGE_KEY = 'app:noova-energy';

  clientSettingsResource = rxResource<ClientSettings, { key: string } | undefined>({
    params: () => {
      const isLoggedIn = this.authService.isLoggedIn();
      return isLoggedIn ? { key: this.STORAGE_KEY } : undefined;
    },
    stream: ({ params }) => {
      return this.loadClientSettings(params.key);
    },
  });

  saveSettings$ = new Subject<ClientSettingsSaveRequest>();
  setValue$ = new Subject<ClientSettingsSetValueRequest>();
  removeValue$ = new Subject<ClientSettingsRemoveValueRequest>();

  constructor() {
    this.saveSettings$
      .pipe(
        switchMap((request) =>
          this.saveClientSettings(this.STORAGE_KEY, request.value).pipe(
            tap({
              next: () => {
                this.settings.set(request.value);
                request.onSuccess?.();
              },
              error: (err) => request.onError?.(err),
            }),
            catchError(() => EMPTY),
          ),
        ),
        takeUntilDestroyed(),
      )
      .subscribe();

    this.setValue$
      .pipe(
        switchMap((request) => {
          const currentSettings = { ...this.settings() };
          this.setNestedValue(currentSettings, request.identifier, request.value);

          return this.saveClientSettings(this.STORAGE_KEY, currentSettings).pipe(
            tap({
              next: () => {
                this.settings.set(currentSettings);
                request.onSuccess?.();
              },
              error: (err) => request.onError?.(err),
            }),
            catchError(() => EMPTY),
          );
        }),
        takeUntilDestroyed(),
      )
      .subscribe();

    this.removeValue$
      .pipe(
        switchMap((request) => {
          const currentSettings = { ...this.settings() };
          this.removeNestedValue(currentSettings, request.identifier);

          return this.saveClientSettings(this.STORAGE_KEY, currentSettings).pipe(
            tap({
              next: () => {
                this.settings.set(currentSettings);
                request.onSuccess?.();
              },
              error: (err) => request.onError?.(err),
            }),
            catchError(() => EMPTY),
          );
        }),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  getValue(identifier: string, defaultValue?: unknown): unknown {
    return this.getNestedValue(this.settings(), identifier) ?? defaultValue;
  }

  private loadClientSettings(key: string): Observable<ClientSettings> {
    return this.http
      .get<string>(CLIENT_SETTINGS_ENDPOINTS.getClientSettings(key))
      .pipe(map((value) => ClientSettings.fromDto({ key, value: value || '' })));
  }

  private saveClientSettings(key: string, value: Record<string, unknown>): Observable<void> {
    const dto = ClientSettings.toDto({ key, value });
    return this.http.post<void>(CLIENT_SETTINGS_ENDPOINTS.saveClientSettings(), dto);
  }

  private getNestedValue(obj: Record<string, unknown>, path: string): unknown {
    return path.split('.').reduce((current: unknown, key: string) => {
      return current && typeof current === 'object' && key in current
        ? (current as Record<string, unknown>)[key]
        : undefined;
    }, obj);
  }

  private setNestedValue(obj: Record<string, unknown>, path: string, value: unknown): void {
    const keys = path.split('.');
    const lastKey = keys.pop()!;

    const target = keys.reduce((current: Record<string, unknown>, key: string) => {
      if (!(key in current) || typeof current[key] !== 'object' || current[key] === null) {
        current[key] = {};
      }
      return current[key] as Record<string, unknown>;
    }, obj);

    target[lastKey] = value;
  }

  private removeNestedValue(obj: Record<string, unknown>, path: string): void {
    const keys = path.split('.');
    const lastKey = keys.pop()!;

    const target = keys.reduce((current: unknown, key: string) => {
      return current && typeof current === 'object' && key in current
        ? (current as Record<string, unknown>)[key]
        : undefined;
    }, obj);

    if (target && typeof target === 'object') {
      delete (target as Record<string, unknown>)[lastKey];
    }
  }
}
